// Déclaration des types pour Hotjar
declare global {
  interface Window {
    hj: (command: string, ...args: any[]) => void;
    _hjSettings: {
      hjid: number;
      hjsv: number;
    };
  }
}

class HotjarService {
  private isLoaded = false;
  private isInitialized = false;
  private readonly HOTJAR_ID = 6548278;
  private readonly HOTJAR_VERSION = 6;

  /**
   * Charge dynamiquement le script Hotjar (seulement quand un utilisateur se connecte)
   * Cette approche permet d'économiser sur les coûts Hotjar
   */
  private loadHotjarScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isLoaded) {
        resolve();
        return;
      }

      try {
        // Créer et configurer le script Hotjar
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://static.hotjar.com/c/hotjar-${this.HOTJAR_ID}.js?sv=${this.HOTJAR_VERSION}`;
        
        // Initialiser les variables globales Hotjar
        window.hj = window.hj || function() {
          (window.hj as any).q = (window.hj as any).q || [];
          (window.hj as any).q.push(arguments);
        };
        window._hjSettings = {
          hjid: this.HOTJAR_ID,
          hjsv: this.HOTJAR_VERSION
        };

        script.onload = () => {
          this.isLoaded = true;
          console.log('🔥 Script Hotjar chargé dynamiquement avec succès');
          resolve();
        };

        script.onerror = () => {
          console.error('❌ Erreur lors du chargement du script Hotjar');
          reject(new Error('Échec du chargement du script Hotjar'));
        };

        // Ajouter le script au head
        document.head.appendChild(script);
      } catch (error) {
        console.error('❌ Erreur lors de la création du script Hotjar:', error);
        reject(error);
      }
    });
  }

  /**
   * Initialise Hotjar pour un utilisateur connecté
   * Cette méthode charge le script et identifie l'utilisateur
   */
  async initializeForUser(email: string, userId: string, attributes?: Record<string, any>): Promise<void> {
    try {
      // Charger le script Hotjar si pas encore fait
      if (!this.isLoaded) {
        await this.loadHotjarScript();
      }

      // Attendre que Hotjar soit complètement initialisé
      await this.waitForHotjarReady();

      // Identifier l'utilisateur
      this.identifyUser(email, userId, attributes);

      this.isInitialized = true;
      console.log('🔥 Hotjar initialisé et utilisateur identifié:', { email, userId });
    } catch (error) {
      console.error('❌ Erreur lors de l\'initialisation Hotjar:', error);
    }
  }

  /**
   * Attend que Hotjar soit complètement chargé et prêt
   */
  private waitForHotjarReady(): Promise<void> {
    return new Promise((resolve) => {
      const checkHotjar = () => {
        if (window.hj && typeof window.hj === 'function') {
          resolve();
        } else {
          setTimeout(checkHotjar, 100);
        }
      };
      checkHotjar();
    });
  }

  /**
   * Identifie un utilisateur dans Hotjar
   */
  private identifyUser(email: string, userId: string, attributes?: Record<string, any>): void {
    if (!this.isInitialized || !window.hj) {
      console.warn('⚠️ Hotjar non initialisé, impossible d\'identifier l\'utilisateur');
      return;
    }

    try {
      window.hj('identify', userId, {
        email: email,
        user_id: userId,
        ...attributes
      });

      console.log('👤 Utilisateur identifié dans Hotjar:', { email, userId });
    } catch (error) {
      console.error('❌ Erreur lors de l\'identification Hotjar:', error);
    }
  }

  /**
   * Réinitialise l'identification de l'utilisateur (lors de la déconnexion)
   * Note: Le script reste chargé pour éviter de le recharger à chaque connexion
   */
  resetUser(): void {
    if (!this.isInitialized || !window.hj) {
      return;
    }

    try {
      window.hj('identify', null);
      console.log('🔄 Identification Hotjar réinitialisée');
    } catch (error) {
      console.error('❌ Erreur lors de la réinitialisation Hotjar:', error);
    }
  }

  /**
   * Envoie un événement personnalisé à Hotjar
   */
  trackEvent(eventName: string, properties?: Record<string, any>): void {
    if (!this.isInitialized || !window.hj) {
      console.warn('⚠️ Hotjar non initialisé, impossible de tracker l\'événement');
      return;
    }

    try {
      window.hj('event', eventName, properties);
      console.log('📊 Événement Hotjar envoyé:', { eventName, properties });
    } catch (error) {
      console.error('❌ Erreur lors du tracking Hotjar:', error);
    }
  }

  /**
   * Déclenche un enregistrement Hotjar
   */
  triggerRecording(): void {
    if (!this.isInitialized || !window.hj) {
      console.warn('⚠️ Hotjar non initialisé, impossible de déclencher l\'enregistrement');
      return;
    }

    try {
      window.hj('trigger', 'authenticated_user');
      console.log('🎥 Enregistrement Hotjar déclenché pour utilisateur connecté');
    } catch (error) {
      console.error('❌ Erreur lors du déclenchement Hotjar:', error);
    }
  }

  /**
   * Vérifie si Hotjar est disponible et initialisé
   */
  isAvailable(): boolean {
    return this.isInitialized && this.isLoaded && typeof window !== 'undefined' && !!window.hj;
  }

  /**
   * Obtient le statut de chargement de Hotjar
   */
  getStatus(): { loaded: boolean; initialized: boolean } {
    return {
      loaded: this.isLoaded,
      initialized: this.isInitialized
    };
  }
}

// Exporter une instance unique du service
const hotjarService = new HotjarService();
export default hotjarService;
